import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardBody, Typography, Select, Option, Spinner } from '@material-tailwind/react';
import { doc, getDoc } from 'firebase/firestore';
import db from '../../firebase';
import { UserAuth } from '../../context/AuthContext';
import { updateDraftDelegation } from '../../services/reportApi';

/**
 * Switch a draft between personal and delegated (on behalf of an agent).
 * Only shown to users with delegateFor claims. Updates ownerUid/onBehalfOf/
 * preparer/teamId together server-side so rendering and access stay consistent.
 */
function DelegationPanel({ draft, onDraftChange, onError }) {
  const { user, delegateFor } = UserAuth();
  const [agentProfiles, setAgentProfiles] = useState({});
  const [myProfile, setMyProfile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const draftId = draft?.draftId;
  const currentValue = draft?.onBehalfOf || 'personal';

  // Load own + agent profiles from user_data
  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      try {
        const uids = [user.uid, ...(delegateFor || [])];
        const results = await Promise.all(uids.map(async (uid) => {
          const snap = await getDoc(doc(db, 'user_data', uid));
          return snap.exists() ? { uid, ...snap.data() } : { uid };
        }));
        const profiles = {};
        for (const p of results) profiles[p.uid] = p;
        setMyProfile(profiles[user.uid] || {});
        setAgentProfiles(profiles);
      } catch (err) {
        console.error('Failed to load delegate profiles:', err);
      }
    };
    load();
  }, [user?.uid, delegateFor]);

  const options = useMemo(() => {
    const opts = [{ value: 'personal', label: `Personal — ${user?.displayName || user?.email || 'me'}` }];
    for (const uid of delegateFor || []) {
      const p = agentProfiles[uid];
      opts.push({ value: uid, label: p?.displayName ? `${p.displayName} (${p.email || ''})` : uid });
    }
    return opts;
  }, [delegateFor, agentProfiles, user?.displayName, user?.email]);

  const handleChange = async (value) => {
    if (!draftId || value === currentValue || isSaving) return;
    try {
      setIsSaving(true);
      let params;
      if (value === 'personal') {
        params = {
          onBehalfOf: null,
          preparer: {
            name: user?.displayName || '',
            email: user?.email || '',
            phone: myProfile?.phoneNumber || '',
            title: myProfile?.title || '',
            dateFormat: myProfile?.dateFormat || 'MM/DD/YYYY',
          },
          teamId: myProfile?.teamId || '',
        };
      } else {
        const p = agentProfiles[value];
        if (!p?.email) {
          onError?.('Agent profile is missing an email address');
          return;
        }
        params = {
          onBehalfOf: value,
          preparer: {
            name: p.displayName || '',
            email: p.email,
            phone: p.phoneNumber || '',
            title: p.title || '',
            dateFormat: p.dateFormat || 'MM/DD/YYYY',
          },
          teamId: p.teamId || '',
        };
      }
      const response = await updateDraftDelegation(draftId, params);
      if (response?.draft && onDraftChange) {
        onDraftChange(response.draft);
      }
    } catch (err) {
      console.error('Failed to update delegation:', err);
      onError?.(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Only relevant for users who can act on behalf of agents
  if (!delegateFor || delegateFor.length === 0) return null;

  return (
    <Card className="mb-4">
      <CardBody className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Typography variant="h6" color="blue-gray">
            Prepared By
          </Typography>
          {isSaving && <Spinner className="h-3.5 w-3.5" />}
        </div>
        <Typography variant="small" className="text-xs text-gray-500 mb-3">
          Switch this draft between your personal reports and a delegated report on
          behalf of an agent. Updates the preparer shown on the report and who the
          report belongs to.
        </Typography>
        <Select
          label="Prepared by"
          value={currentValue}
          onChange={handleChange}
          disabled={isSaving}
        >
          {options.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </CardBody>
    </Card>
  );
}

export default DelegationPanel;
